import { useEffect, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useContracts } from './useContract';
import { REFRESH_INTERVAL } from '../config/appConfig';

// Loads current user's ID + basic + extended info from Views.
// NO useCallback anywhere. Uses inline async in effects to avoid dependency loops.
export const useUserData = () => {
  const { account, rightNetwork } = useWeb3();
  const { viewsR } = useContracts();

  const [loading, setLoading]     = useState(false);
  const [userId, setUserId]       = useState(0);
  const [basic, setBasic]         = useState(null);
  const [extended, setExtended]   = useState(null);
  const [pendingRoi, setPendingRoi] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!account || !viewsR || !rightNetwork) {
        setUserId(0);
        setBasic(null);
        setExtended(null);
        setPendingRoi(null);
        return;
      }
      setLoading(true);
      try {
        const id = await viewsR.getUserIdByAddress(account);
        if (cancelled) return;
        const idNum = id.toNumber();
        setUserId(idNum);

        if (idNum === 0) {
          setBasic(null);
          setExtended(null);
          setPendingRoi(null);
        } else {
          const [b, e, p] = await Promise.all([
            viewsR.getUserBasicInfo(idNum),
            viewsR.getUserExtendedInfo(idNum),
            viewsR.getUserPendingROI(idNum).catch(() => null)
          ]);
          if (cancelled) return;
          setBasic(b);
          setExtended(e);
          setPendingRoi(p);
        }
      } catch (err) {
        // silent — keep prior state
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();

    // poll
    const t = setInterval(run, REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [account, viewsR, rightNetwork, refreshTick]);

  return {
    loading,
    userId,
    isRegistered: userId > 0,
    basic,
    extended,
    pendingRoi,
    refresh
  };
};
